import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControlLabel,
  Switch,
  CircularProgress,
  Tooltip,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { orderReportingPeriodService } from '../services/api';

function normList(res) {
  const d = res.data;
  return Array.isArray(d?.results) ? d.results : Array.isArray(d) ? d : [];
}

export default function OrderReportingPeriodsPage() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({
    name: '',
    starts_on: new Date().toISOString().slice(0, 10),
    visible_in_app: true,
  });

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const res = await orderReportingPeriodService.getAll();
      setRows(normList(res));
    } catch (e) {
      console.error(e);
      alert('Помилка завантаження періодів');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const openNew = () => {
    setEditing(null);
    setForm({
      name: String(new Date().getFullYear()),
      starts_on: `${new Date().getFullYear()}-01-01`,
      visible_in_app: true,
    });
    setDialogOpen(true);
  };

  const openEdit = (row) => {
    setEditing(row.id);
    setForm({
      name: row.name || '',
      starts_on: row.starts_on || '',
      visible_in_app: row.visible_in_app !== false,
    });
    setDialogOpen(true);
  };

  const closeDialog = () => {
    setDialogOpen(false);
    setEditing(null);
  };

  const handleSave = async () => {
    if (!form.name.trim()) {
      alert('Вкажіть назву періоду');
      return;
    }
    if (!form.starts_on) {
      alert('Вкажіть дату початку');
      return;
    }
    const payload = {
      name: form.name.trim(),
      starts_on: form.starts_on,
      visible_in_app: form.visible_in_app,
    };
    try {
      if (editing) {
        await orderReportingPeriodService.update(editing, payload);
      } else {
        await orderReportingPeriodService.create(payload);
      }
      closeDialog();
      load();
    } catch (e) {
      console.error(e);
      const msg =
        e.response?.data &&
        (typeof e.response.data === 'string'
          ? e.response.data
          : JSON.stringify(e.response.data, null, 2));
      alert(msg || e.message || 'Помилка збереження');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Видалити звітний період? Якщо є прив’язані замовлення, операція не вдасться.')) return;
    try {
      await orderReportingPeriodService.delete(id);
      load();
    } catch (e) {
      console.error(e);
      alert(e.response?.data?.detail || e.message || 'Помилка видалення');
    }
  };

  const toggleVisible = async (row) => {
    try {
      await orderReportingPeriodService.patch(row.id, { visible_in_app: !row.visible_in_app });
      load();
    } catch (e) {
      console.error(e);
      alert(e.response?.data?.detail || e.message || 'Помилка оновлення');
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="320px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Box>
          <Typography variant="h4" gutterBottom>
            Звітні періоди замовлень
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 720 }}>
            Для кожної дати замовлення обирається період з найпізнішою датою початку ≤ дати замовлення.
            Нумерація нових замовлень у межах періоду починається з 1. Якщо вимкнути «Показувати в
            системі», замовлення цього періоду зникнуть зі списків і вибору (дані збережені в базі).
          </Typography>
        </Box>
        <Button variant="contained" startIcon={<AddIcon />} onClick={openNew}>
          Новий період
        </Button>
      </Box>

      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Назва</TableCell>
              <TableCell>Початок</TableCell>
              <TableCell align="center">Видимий</TableCell>
              <TableCell align="right">Дії</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={4}>
                  <Typography color="text.secondary">Періодів ще немає — додайте перший.</Typography>
                </TableCell>
              </TableRow>
            ) : (
              rows.map((r) => (
                <TableRow key={r.id} hover>
                  <TableCell>{r.name}</TableCell>
                  <TableCell>{r.starts_on}</TableCell>
                  <TableCell align="center">
                    <Tooltip title="Натисніть, щоб змінити видимість у всій системі">
                      <Switch
                        checked={r.visible_in_app !== false}
                        onChange={() => toggleVisible(r)}
                        color="primary"
                      />
                    </Tooltip>
                  </TableCell>
                  <TableCell align="right">
                    <IconButton size="small" onClick={() => openEdit(r)} aria-label="Редагувати">
                      <EditIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => handleDelete(r.id)}
                      aria-label="Видалити"
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={dialogOpen} onClose={closeDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{editing ? 'Редагувати період' : 'Новий період'}</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
          <TextField
            label="Назва (наприклад, 2026)"
            value={form.name}
            onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
            fullWidth
          />
          <TextField
            label="Дата початку періоду"
            type="date"
            value={form.starts_on}
            onChange={(e) => setForm((p) => ({ ...p, starts_on: e.target.value }))}
            fullWidth
            InputLabelProps={{ shrink: true }}
            helperText="Унікальна дата: для одного дня не можна створити два періоди."
          />
          <FormControlLabel
            control={
              <Switch
                checked={form.visible_in_app}
                onChange={(e) => setForm((p) => ({ ...p, visible_in_app: e.target.checked }))}
              />
            }
            label="Показувати замовлення періоду в системі"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog}>Скасувати</Button>
          <Button variant="contained" onClick={handleSave}>
            Зберегти
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
